// --- external

// --- internal
import { useI18n } from "../system";
import { useBrand } from "../brand";

// --- utils
import {
  useTranslateName,
  DetailedError,
  responseCodes,
  parseError,
  ErrorOrigin
} from "../../utils";
import {
  useUischemaTitle,
  useProductName,
  parseProductDetails,
  parseBasketProductModel
} from "../product/utils";

import {
  concat,
  find,
  findLast,
  first,
  forEach,
  get,
  has,
  isEmpty,
  isNil,
  isObject,
  isObjectLike,
  map,
  mapValues,
  omitBy,
  reduce,
  set,
  some,
  values
} from "lodash-es";

// --- types
import type { ErrorObject } from "ajv";

import type {
  IBasket,
  IBasketProduct,
  IBasketPromotion,
  IPromotion
} from "@upmind-automation/types";
import {
  ProductOrderTypes,
  PromotionDisplayTypes
} from "@upmind-automation/types";

import type {
  BasketProduct,
  IBasketProductModel,
  IBasketSubproductModel
} from "./types";

import type {
  PromotionDetails,
  ProductModel,
  SubproductModel,
  ProductSummaryDetailWithPrice,
  ProductSummaryDetail,
  PriceDetail,
  ExternalError,
  ProductProps
} from "../product";

// -----------------------------------------------------------------------------

export const parseBasketProduct = (
  raw: IBasketProduct,
  basketErrors?: any
): BasketProduct => {
  const errors = get(basketErrors, raw?.id);
  // Get price object matching `display_price_billing_cycle_months`
  const pricing = parsSummaryWithPrice(raw);

  const basketProduct: BasketProduct = {
    id: raw.id,
    serviceIdentifier: raw?.service_identifier ?? undefined,

    // --- model/configuration
    configuration: parseBasketProductModel(raw),

    // --- product details
    productDetails: parseProductDetails(raw.product),

    // --- meta details
    meta: pricing.meta,
    // --- summary details
    price: pricing.price,
    pricing: [pricing], // may be added to below
    details: [], // will be built up below

    // --- errors
    // TODO: check the errors provided and map correctly
    errors: omitBy(errors, isEmpty)
  };

  // --- because we are a full basket product, we may have a service identifier
  //     so we should regenerate the product title
  basketProduct.productDetails.title = useUischemaTitle(raw.product, {
    basketProduct: raw,
    valueKey: "meta.uischema.title",
    fallback: useProductName(raw.product, raw)
  });

  // --- Now build up our details
  const term = parseTermSummary(raw);
  if (term) {
    basketProduct.details.push(term);
  }
  // ---
  forEach(raw?.options, option => {
    const subproduct = parsSummaryWithPrice(option);
    if (subproduct) {
      // Add our non-quantifiable pricing
      if (option.product.order_type === ProductOrderTypes.SINGLE_OPTION)
        basketProduct.pricing.push(subproduct);

      subproduct.name = "option";
      basketProduct.details.push(subproduct as ProductSummaryDetailWithPrice);
    }
  });

  // ---
  forEach(raw?.attributes, attribute => {
    const subproduct = parseSummary(attribute);
    if (subproduct) {
      subproduct.name = "attribute";
      basketProduct.details.push(subproduct as ProductSummaryDetail);
    }
  });

  // ---
  forEach(raw?.provision_fields, (value, key) => {
    const fieldError = find(errors?.provisionFields, ["propertyName", key]);
    const field = parseProvisionFieldSummary(key.toString(), value, fieldError);
    if (field) basketProduct.details.push(field);
  });

  // ---

  return basketProduct;
};

export function parseSummary(subproduct: IBasketProduct): ProductSummaryDetail {
  return {
    id: subproduct.product_id,
    name: subproduct.product.name,
    title: useUischemaTitle(subproduct.product, {
      basketProduct: subproduct,
      valueKey: "meta.uischema.title",
      fallback: useProductName(subproduct.product, subproduct)
    }),
    category: useTranslateName(subproduct.product.category),
    cycle: subproduct.billing_cycle_months,
    quantity: subproduct.quantity,
    meta: {}
  };
}

export function parsSummaryWithPrice(
  raw: IBasketProduct
): ProductSummaryDetailWithPrice {
  const { includesTax } = useBrand();

  const summary = parseSummary(raw) as Partial<ProductSummaryDetailWithPrice>;

  summary.meta = {
    oneoff: raw.billing_cycle_months > 0,
    discounted: raw.configuration_net_amount_discount_converted > 0,
    free: raw.configuration_net_amount_discounted_converted == 0,
    overrides: raw?.product?.category?.price_override,
    mixed: raw?.product?.mixed_promotions, //TODO: check if this is correct
    includesTax: includesTax.value
  };

  summary.promotions = parsePromotionDetails(raw);
  summary.price = parsPrice(raw);

  return summary as ProductSummaryDetailWithPrice;
}

export function parsPrice(raw: IBasketProduct): PriceDetail {
  const { includesTax } = useBrand();

  const discounted = raw.configuration_net_amount_discount_converted > 0;

  const regularAmount = includesTax.value
    ? raw.configuration_total_amount_converted
    : raw.configuration_net_amount_converted;
  const regularPrice = includesTax.value
    ? raw.configuration_total_amount_formatted
    : raw.configuration_net_amount_formatted;
  //  ---
  const currentAmount = includesTax.value
    ? raw.configuration_total_discounted_amount_converted
    : raw.configuration_net_amount_discounted_converted;
  const currentPrice = includesTax.value
    ? raw.configuration_total_discounted_amount_formatted
    : raw.configuration_net_amount_discounted_formatted;
  // ---
  const savingAmount = includesTax.value
    ? raw.configuration_total_discount_amount_converted
    : raw.configuration_net_amount_discount_converted; //TODO: MISSING net price discount
  const savingPrice = includesTax.value
    ? raw.configuration_total_discount_amount_formatted
    : raw.configuration_net_amount_discount_formatted;

  const savingPercent = discounted
    ? `${Math.round((savingAmount / regularAmount) * 100)}%`
    : "";

  const unit =
    raw.quantity > 1
      ? {
          total: includesTax.value
            ? raw.selling_price_converted
            : raw.net_selling_price_discounted_converted,
          totalFormatted: includesTax.value
            ? raw.selling_price_formatted
            : raw.net_selling_price_discounted_formatted,
          subtotal: includesTax.value
            ? raw.selling_price_converted
            : raw.net_selling_price,
          subtotalFormatted: includesTax.value
            ? raw.selling_price_formatted
            : raw.net_selling_price_formatted
        }
      : undefined;

  const configuration = {
    total: includesTax.value ? raw.total_amount : raw.net_amount,
    totalFormatted: includesTax.value
      ? raw.total_amount_formatted
      : raw.net_amount_formatted,
    subtotal: includesTax.value ? raw.total_amount : raw.net_selling_price,
    subtotalFormatted: includesTax.value
      ? raw.total_amount_formatted
      : raw.net_selling_price_formatted,
    discount: includesTax.value
      ? raw.total_discount_amount
      : raw.net_product_discount_amount,
    discountFormatted: includesTax.value
      ? raw.total_discount_amount_formatted
      : raw.net_product_discount_amount_formatted
  };

  return {
    regularAmount,
    regularPrice,
    currentAmount,
    currentPrice,
    savingAmount,
    savingPrice,
    savingPercent,
    // Base prices (always excluding tax, e.g for summary displays)
    baseAmount: raw.configuration_net_amount_converted,
    basePrice: raw.configuration_net_amount_formatted,
    unit,
    configuration
  } as PriceDetail;
}

export const parsePromotionDetails = (
  raw: IBasketProduct
): PromotionDetails[] => {
  //  Basket Product Promotions can only be displayed in one of 2 ways:
  //  - As a generic summary label with no values when mixed, eg "SAVE"
  //  - As a summary percentage, eg "Save 20%"
  // NB: we always supply the amounts so we can show meta data if needed, eg a tooltip

  const price = parsPrice(raw);

  if (!price.savingAmount) return [];

  return [
    {
      code: "save",
      name: "save", // untranslated name for reporting purposes  category?: string;
      title: "Save",
      meta: {
        display: PromotionDisplayTypes.PERCENTAGE,
        mixed: raw.product.mixed_promotions,
        discounted: !!price.savingAmount || raw.product.mixed_promotions
      },
      price: {
        savingAmount: price.savingAmount,
        savingPrice: price.savingPrice,
        savingPercent: price.savingPercent
      }
    } as PromotionDetails
  ];
};

export function parseTermSummary(
  raw: IBasketProduct
): ProductSummaryDetailWithPrice {
  const summary = parsSummaryWithPrice(raw) as ProductSummaryDetailWithPrice;

  summary.name = "term";

  //  Allow for "price-overrrides"
  set(summary, "meta.free", raw.net_amount == 0);
  set(summary, "meta.overriden", raw.net_amount == 0);

  // if we have no price then we are being overridden, so we need to force the currentPricing to 0
  if (raw.net_amount == 0) {
    set(summary, "price.currentAmount", raw.net_amount);
    set(summary, "price.currentPrice", "");
  }

  return summary;
}

export function parseProvisionFieldSummary(
  key: string,
  data: any,
  error?: ExternalError["provisionFields"]
): ProductSummaryDetail {
  const title = get(data, key, data); // just in case its an object > unti lwe have types
  return {
    name: `provision_field.${key}`,
    category: key,
    title,
    error,
    meta: {
      invalid: !!error
    }
  };
}

export function parsePromotionsOrCoupons(
  promotions?: IBasketPromotion[] | string[]
): string[] {
  return map(promotions, basketPromotion => {
    const promocode: string = has(basketPromotion, "promotion")
      ? (basketPromotion as IBasketPromotion).promotion.code
      : (basketPromotion as string);

    return promocode;
  });
}

export function parseBasketProductData(
  model: ProductProps,
  clean?: boolean
): IBasketProductModel {
  const data: IBasketProductModel = {
    product_id: model.productId,
    quantity: model.quantity,
    billing_cycle_months: model.term ?? 0,
    // ---
    attributes: parseBasketSubproductConfig(model?.attributes),
    options: parseBasketSubproductConfig(model?.options),
    // ---
    provision_field_values: model.provisionFields || {},
    provision_field_values_validate: !model.silent, // suppress prov field validation errors if silent is true
    // ---
    promotions: map(model.coupons, coupon => ({ promocode: coupon }))
  };

  if (!clean) return data;

  return omitBy(data, value => {
    if (isObjectLike(value)) {
      return isEmpty(omitBy(value as object, isEmpty));
    }
    return isNil(value);
  }) as IBasketProductModel;
}

export function parseBasketSubproductConfig(
  subproducts?: SubproductModel
): IBasketSubproductModel[] {
  return reduce(
    subproducts ?? {},
    (result: IBasketSubproductModel[], subproduct) => {
      if (subproduct) {
        const selected = values(
          mapValues(subproduct, choice => {
            return {
              product_id: choice.productId,
              unit_quantity: choice.quantity,
              billing_cycle_months: choice.cycle
            };
          })
        );
        if (!isEmpty(selected)) {
          result.push(...selected);
        }
      }
      return result;
    },
    []
  );
}

export function getBasketProduct(id: string, basket: IBasket) {
  const { t } = useI18n();
  const value = find(basket?.products, { id });
  if (!value) {
    throw new DetailedError(
      t("error.basket_product_not_found"),
      responseCodes.Not_Found,
      ErrorOrigin.Headless
    );
  }

  return value;
}

export function parseBasketProductError(
  rawError: any | any[]
): Record<string, ErrorObject[]> {
  const error = {
    term: reduce(
      rawError?.term,
      (result: ErrorObject[], value, key) => {
        const parsed = parseError(value, key);
        return concat(result, parsed);
      },
      []
    ),
    options: reduce(
      rawError?.options,
      (result: ErrorObject[], value, key) => {
        const parsed = parseError(value, key);
        return concat(result, parsed);
      },
      []
    ),
    attributes: reduce(
      rawError?.attributes,
      (result: ErrorObject[], value, key) => {
        const parsed = parseError(value, key);
        return concat(result, parsed);
      },
      []
    ),
    provisionFields: reduce(
      rawError?.provision_field_values,
      (result: ErrorObject[], value, key) => {
        const parsed = parseError(value, key);
        return concat(result, parsed);
      },
      []
    )
  };
  return omitBy(error, isEmpty) as Record<string, ErrorObject[]>;
}

/**
 * This allows us to dynamically inject values from our basket products
 * into the product model ( currently only provision fields) based on
 * template literals, eg  {{ service_identifier}} or {{provisioning_fields.someProperty}}
 * eg:
 *    This is epecially useful for setting things like the domain name for a hosting product based on a domain product
 * @param model
 * @param products  // The basket products that we can use to resolve dynamic values
 * @returns ProductModel | undefined
 */
export function transformProductDynamicValues(
  model: ProductModel,
  products: IBasketProduct[]
): ProductModel | undefined {
  if (!model || !model?.productId) return undefined;

  model.provisionFields = mapValues(
    model?.provisionFields ?? {},
    (value: any) => {
      // get any dynamic properties that we need to resolve
      const dynamicProperty: string = first(value.match(/(?<=\$\{).+?(?=\})/));
      const product = findLast(products, product =>
        has(product, dynamicProperty)
      );
      if (dynamicProperty && product) {
        return get(product, dynamicProperty, null);
      }
      return value;
    }
  );
  return model;
}
